/**
 * Pure decisions for the public aggregate surfaces.
 *
 * Every number a donor-facing page shows about other people passes through
 * here first. The module is pure on purpose: the same decision the API made
 * about a group is re-made at the drawing board, because the two places that
 * can leak a count are the place that builds it and the place that renders it,
 * and neither may trust the other.
 *
 * ## The blind band (phase 5 design, section e)
 *
 * A group of contributors smaller than the threshold is never reported as a
 * number. "3 contributors" narrows the donors to three people; "fewer than
 * five" does not. The number itself is the leak, so inside the band there is
 * no count at all - not a rounded one, not a zero.
 */

/**
 * The smallest group a public surface may count.
 *
 * The server's copy of this setting is `privacy_min_group_size` (seeded 5,
 * section e). The web holds the same floor as a constant so a page can say
 * "fewer than five" without knowing what the exact count was - the one thing
 * it must never learn.
 */
export const PRIVACY_MIN_GROUP_SIZE = 5;

/** True when a group of this size is too small to publish a count for. */
export function isSmallGroup(
  count: number,
  minGroupSize: number = PRIVACY_MIN_GROUP_SIZE,
): boolean {
  return count < minGroupSize;
}

/**
 * What a public surface shows for a group of contributors.
 *
 * `smallGroup` is the blind band: the count is withheld entirely, and the page
 * answers with "fewer than {{minGroupSize}}". A group at or above the
 * threshold carries its real count, because hiding an honest number would be
 * its own kind of dishonesty.
 */
export type ContributorCount =
  | { readonly smallGroup: true }
  | { readonly smallGroup: false; readonly count: number };

export function contributorCount(
  count: number,
  minGroupSize: number = PRIVACY_MIN_GROUP_SIZE,
): ContributorCount {
  return isSmallGroup(count, minGroupSize) ? { smallGroup: true } : { smallGroup: false, count };
}

/**
 * The money line of a campaign or a page.
 *
 * `null` is "nothing has come in yet" - the honest empty state that invites
 * the first gift. It is deliberately not zero: zero reads as "this has been
 * tried and failed", and a campaign that nobody has reached yet has not
 * failed. A zero that arrives with a real aggregate (money came in and went
 * back out) keeps its zero.
 */
export type RaisedState =
  | { readonly nothingYet: true }
  | { readonly nothingYet: false; readonly amountCents: number };

export function raisedState(raisedCents: number | null): RaisedState {
  return raisedCents === null
    ? { nothingYet: true }
    : { nothingYet: false, amountCents: raisedCents };
}

/**
 * Progress towards a goal as a whole percent, floored at 0 and capped at 100.
 *
 * The cap is honesty too: an overshoot is real, but a bar that draws past its
 * own track reads as a bug, and the number beside it still says the truth. A
 * goal of nothing divides nothing - the guard keeps that from being NaN.
 */
export function goalPercent(done: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((done / goal) * 100)));
}
